"use client";

import React from "react";
import { Activity, ArrowRight, ChevronRight, CircleCheck as CheckCircle2, FileCheck2, FlaskConical, Gauge, Layers, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const metrics = [{
  id: 1,
  value: "Complete",
  label: "Compliance & Traceability",
  description: "Structured audit trails and data integrity documentation for regulated processes"
}, {
  id: 2,
  value: "< 0.05%",
  label: "Process Variance",
  description: "Tight in-line control for high-yield chemical synthesis"
}, {
  id: 3,
  value: "Ongoing",
  label: "Technical Lifecycle Support",
  description: "Dedicated qualification engineering in Ireland and the UK"
}];
const keyFeatures = [{
  id: "pat",
  title: "Process Analytical Technology (PAT)",
  description: "Real-time reaction monitoring and automated endpoint verification.",
  icon: Activity
}, {
  id: "qual",
  title: "Validated Qualification (IQ/OQ/PQ)",
  description: "Comprehensive qualification protocols designed for strict regulatory compliance.",
  icon: ShieldCheck
}, {
  id: "rec",
  title: "Solvent Recovery & Safety Monitoring",
  description: "Closed-loop analytical verification to ensure facility safety and material efficiency.",
  icon: Gauge
}];
export default function ChemicalHero() {
  return <section id="chemicalmanufacturinghero" data-nav="light" className="relative bg-background text-foreground overflow-hidden pt-12 pb-20 lg:pt-20 lg:pb-28 border-b border-border">
      {/* Background Architectural Patterns */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-[0.03] [background-size:32px_32px] [background-image:linear-gradient(to_right,currentColor_1px,transparent_1px),linear-gradient(to_bottom,currentColor_1px,transparent_1px)]" />

      {/* Subtle safety accent ambient glow */}
      <div aria-hidden="true" className="absolute -top-32 right-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-3xl pointer-events-none" />
      <div aria-hidden="true" className="absolute top-1/2 -left-48 w-[400px] h-[400px] bg-secondary/60 rounded-full blur-3xl pointer-events-none" />

      <div className="page-container relative z-10">
        {/* Breadcrumb navigation */}
        <nav aria-label="Breadcrumb" className="mb-6 flex items-center text-xs text-muted-foreground">
          <ol className="flex items-center gap-1.5 flex-wrap">
            <li>
              <Link to="/HomePage" className="hover:text-foreground transition-colors">
                Home
              </Link>
            </li>
            <li aria-hidden="true" className="text-muted-foreground/60">
              <ChevronRight className="size-3.5" />
            </li>
            <li>
              <Link to="/IndustriesPage" className="hover:text-foreground transition-colors">
                Industries
              </Link>
            </li>
            <li aria-hidden="true" className="text-muted-foreground/60">
              <ChevronRight className="size-3.5" />
            </li>
            <li>
              <span className="font-medium text-foreground" aria-current="page">
                Chemical Manufacturing
              </span>
            </li>
          </ol>
        </nav>

        {/* Top Header Block */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center mb-16 lg:mb-20">
          <div className="lg:col-span-7 flex flex-col gap-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 font-mono text-xs uppercase tracking-wide gap-1.5 border border-border">
                <FlaskConical className="size-3.5 text-primary" />
                Chemical Manufacturing &amp; Scale-Up
              </Badge>
              <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground font-medium px-2 py-0.5 rounded-sm bg-muted">
                <FileCheck2 className="size-3 text-primary" />
                cGMP / ISO 9001 Compliant
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-default font-bold text-foreground tracking-tight leading-[1.1]">
              Analytical precision for high-scale chemical processing.
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Application-led analytical technology, qualification, and lifecycle support for chemical synthesis, bulk active ingredients, and regulated processing plants across Ireland and the UK.
            </p>

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Button asChild size="lg" className="font-semibold shadow-sm">
                <Link to="/ContactPage">
                  Request Technical Consultation
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/SolutionsPage">Explore PAT Solutions</Link>
              </Button>
            </div>

            {/* Quick trust checklist */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4 border-t border-border/80">
              <div className="flex items-center gap-2 text-xs font-medium text-foreground">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>ATEX &amp; Hazardous Area Ready</span>
              </div>
              <div className="flex items-center gap-2 text-xs font-medium text-foreground">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Full IQ/OQ Documentation</span>
              </div>
              <div className="flex items-center gap-2 text-xs font-medium text-foreground">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>UK &amp; Ireland Field Service</span>
              </div>
            </div>
          </div>

          {/* Hero Visual Showcase */}
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden border border-border shadow-xl bg-card">
              <div className="relative aspect-[4/3] sm:aspect-[16/11] w-full overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/chemical_manufacturing_facility.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/chemical_manufacturing_facility.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/chemical_manufacturing_facility.webp-wvc-sizes" alt="Industrial chemical manufacturing and analytical instrumentation setup" className="w-full h-full object-cover" loading="eager" decoding="async" />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent" />

                {/* Floating telemetry card */}
                <div className="absolute bottom-4 left-4 right-4 p-4 rounded-xl bg-card/95 backdrop-blur-md border border-border shadow-lg text-card-foreground">
                  <div className="flex items-center justify-between pb-2 border-b border-border text-xs">
                    <span className="font-semibold flex items-center gap-1.5 text-foreground">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                      </span>
                      Live Process Analytics
                    </span>
                    <span className="text-muted-foreground font-mono">Stream 04 • Validated</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 pt-3">
                    <div>
                      <p className="text-[11px] text-muted-foreground">Endpoint Conversion</p>
                      <p className="text-lg font-bold font-mono text-foreground">99.82%</p>
                    </div>
                    <div>
                      <p className="text-[11px] text-muted-foreground">Solvent Purity Index</p>
                      <p className="text-lg font-bold font-mono text-primary">Class 1 Spec</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Industrial badge floating element */}
            <div className="hidden sm:flex absolute -bottom-5 -left-5 p-3.5 rounded-xl bg-secondary border border-border shadow-md items-center gap-3">
              <div className="size-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                <Layers className="size-5" />
              </div>
              <div>
                <p className="text-xs font-semibold text-secondary-foreground">Multi-Site Scale-Up</p>
                <p className="text-[11px] text-muted-foreground">Standardized cross-facility methods</p>
              </div>
            </div>
          </div>
        </div>

        {/* Feature Cards Matrix */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6 border-t border-border">
          {keyFeatures.map((feature, i) => {
          const IconComponent = feature.icon;
          return <div key={feature.id} data-index={i} className="group relative flex flex-col p-6 rounded-xl bg-card border border-border shadow-xs hover:shadow-md transition-all duration-200">
                <div className="size-11 rounded-lg bg-secondary flex items-center justify-center text-foreground mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <IconComponent className="size-5" />
                </div>
                <h2 className="text-base font-semibold text-card-foreground mb-2 font-default">
                  {feature.title}
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </div>;
        })}
        </div>

        {/* Key Operational Impact Metrics */}
        <div className="mt-8 rounded-2xl bg-secondary/80 border border-border p-6 sm:p-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 divide-y sm:divide-y-0 sm:divide-x divide-border">
            {metrics.map((metric, i) => <div key={metric.id} data-index={i} className={`flex flex-col ${i !== 0 ? "pt-6 sm:pt-0 sm:pl-8" : ""}`}>
                <div className="text-3xl sm:text-4xl font-extrabold text-foreground font-mono tracking-tight text-primary">
                  {metric.value}
                </div>
                <div className="text-sm font-semibold text-foreground mt-1">
                  {metric.label}
                </div>
                <p className="text-xs text-muted-foreground mt-1 leading-normal">
                  {metric.description}
                </p>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}